(ns frontend.view.settings2.security.mcp-admin.events
  "Re-frame events for the MCP admin token management view"
  (:require
   [fx.ajax :as ajax]
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.view.settings2.security.mcp-admin.sub :as sub]
   [re-frame.core :as rf]))

(defn- error-message
  [err fallback]
  (or (:message err)
      (get-in err [:response :message])
      fallback))

(rf/reg-event-fx
 ::init
 (fn [{db :db} _]
   {:db (assoc-in db sub/root-path
                  {:loading?              true
                   :connections           []
                   :error                 nil
                   :revoking-id           nil
                   :revoking-all?         false
                   :revoke-error          nil
                   :sessions-loading?     true
                   :sessions              []
                   :sessions-error        nil
                   :terminating-session-id nil
                   :undo-tx-id-input      ""
                   :undo-preview          nil
                   :undo-loading?         false
                   :undo-error            nil
                   :undo-result           nil
                   :permissions-loading?  true
                   :permissions-saving?   false
                   :permissions-error     nil
                   :permissions-data      {:tools []
                                           :teams []
                                           :users []}
                   :permissions-tab       :teams
                   :selected-team-id      nil
                   :selected-user-id      nil})
    :fx [[:dispatch [::load-connections]]
         [:dispatch [::load-sessions]]
         [:dispatch [::load-tool-permissions]]]}))

(rf/reg-event-fx
 ::cleanup
 (fn [{db :db} _]
   {:db (update db ::sub/state (constantly nil))}))

(rf/reg-event-db
 ::set-permissions-tab
 (fn [db [_ tab]]
   (assoc-in db (conj sub/root-path :permissions-tab) tab)))

(rf/reg-event-db
 ::select-team
 (fn [db [_ team-id]]
   (assoc-in db (conj sub/root-path :selected-team-id) team-id)))

(rf/reg-event-db
 ::select-user
 (fn [db [_ user-id]]
   (assoc-in db (conj sub/root-path :selected-user-id) user-id)))

(rf/reg-event-fx
 ::load-tool-permissions
 (fn [{db :db} [_ {:keys [quiet?]}]]
   {:db (cond-> (assoc-in db (conj sub/root-path :permissions-error) nil)
          (not quiet?)
          (assoc-in (conj sub/root-path :permissions-loading?) true))
    ::ajax/send {:operation :mcp-permissions/load
                 :body      {}
                 :on-ok     [::on-tool-permissions-loaded]
                 :on-error  [::on-tool-permissions-error]}}))

(defn- existing-id-or-first
  [items selected-id]
  (or (some (fn [item]
              (when (= (:id item) selected-id)
                selected-id))
            items)
      (-> items first :id)))

(rf/reg-event-fx
 ::on-tool-permissions-loaded
 (fn [{db :db} [_ resp]]
   (let [data (or (:body resp)
                  {:tools []
                   :teams []
                   :users []})
         selected-team-id (existing-id-or-first
                           (:teams data)
                           (get-in db (conj sub/root-path :selected-team-id)))
         selected-user-id (existing-id-or-first
                           (:users data)
                           (get-in db (conj sub/root-path :selected-user-id)))]
     {:db (-> db
              (assoc-in (conj sub/root-path :permissions-loading?) false)
              (assoc-in (conj sub/root-path :permissions-data) data)
              (assoc-in (conj sub/root-path :selected-team-id) selected-team-id)
              (assoc-in (conj sub/root-path :selected-user-id) selected-user-id))})))

(rf/reg-event-fx
 ::on-tool-permissions-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to load MCP tool permissions" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :permissions-loading?) false)
            (assoc-in (conj sub/root-path :permissions-error)
                      (error-message err "Failed to load MCP tool permissions")))}))

(defn- update-team-in-data
  [data team]
  (update data :teams
          (fn [teams]
            (mapv (fn [existing-team]
                    (if (= (:id existing-team) (:id team))
                      team
                      existing-team))
                  teams))))

(defn- team-with-tool-permission
  [team {:keys [tool-name permission]}]
  (update team :tool-permissions
          (fn [tool-permissions]
            (if (= permission :mcp.tool/default)
              (dissoc tool-permissions tool-name)
              (assoc tool-permissions tool-name permission)))))

(rf/reg-event-fx
 ::set-team-tool-permission
 (fn [{db :db} [_ team-id tool-name permission]]
   (let [data (get-in db (conj sub/root-path :permissions-data))
         team (->> (:teams data)
                   (filter (fn [candidate]
                             (= (:id candidate) team-id)))
                   first)
         updated-team (team-with-tool-permission
                       team
                       {:tool-name  tool-name
                        :permission permission})]
     {:db (-> db
              (assoc-in (conj sub/root-path :permissions-data)
                        (update-team-in-data
                         data
                         updated-team))
              (assoc-in (conj sub/root-path :permissions-saving?) true)
              (assoc-in (conj sub/root-path :permissions-error) nil))
      ::ajax/send {:operation :mcp-permissions/save-team-permissions
                   :body      {:team-id     team-id
                               :permissions (:tool-permissions updated-team)}
                   :on-ok     [::on-team-tool-permissions-saved]
                   :on-error  [::on-tool-permissions-save-error]}})))

(rf/reg-event-fx
 ::on-team-tool-permissions-saved
 (fn [{db :db} [_ resp]]
   (let [team (-> resp :body :team)]
     {:db (cond-> (assoc-in db (conj sub/root-path :permissions-saving?) false)
            team
            (assoc-in (conj sub/root-path :permissions-data)
                      (update-team-in-data
                       (get-in db (conj sub/root-path :permissions-data))
                       team)))
      :fx [[:dispatch [::load-tool-permissions {:quiet? true}]]]})))

(rf/reg-event-fx
 ::on-tool-permissions-save-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to save MCP tool permissions" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :permissions-saving?) false)
            (assoc-in (conj sub/root-path :permissions-error)
                      (error-message err "Failed to save MCP tool permissions")))}))

;;
;; Load all MCP connections
;;

(rf/reg-event-fx
 ::load-connections
 (fn [_ _]
   {::ws-load-connections {}}))

(rf/reg-fx
 ::ws-load-connections
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/list-all-mcp-connections
     :body       {}
     :on-success [::on-connections-loaded]
     :on-error   [::on-load-error]})))

(rf/reg-event-fx
 ::on-connections-loaded
 (fn [{db :db} [_ resp]]
   {:db (-> db
            (assoc-in (conj sub/root-path :loading?) false)
            (assoc-in (conj sub/root-path :connections) (-> resp :body :connections)))}))

(rf/reg-event-fx
 ::on-load-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to load MCP connections" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :loading?) false)
            (assoc-in (conj sub/root-path :error) (error-message err "Failed to load connections")))}))

;;
;; Revoke single connection
;;

(rf/reg-event-fx
 ::revoke-connection
 (fn [{db :db} [_ connection-id]]
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking-id) connection-id)
            (assoc-in (conj sub/root-path :revoke-error) nil))
    ::ws-revoke-connection {:connection-id connection-id}}))

(rf/reg-fx
 ::ws-revoke-connection
 (fn [{:keys [connection-id]}]
   (ws/ws-send!
    {:id         :plugin/revoke-mcp-connection-admin
     :body       {:connection-id connection-id}
     :on-success [::on-connection-revoked]
     :on-error   [::on-revoke-error]})))

(rf/reg-event-fx
 ::on-connection-revoked
 (fn [{db :db} _]
   {:db (assoc-in db (conj sub/root-path :revoking-id) nil)
    :fx [[:dispatch [::load-connections]]
         [:dispatch [::load-sessions]]]}))

(rf/reg-event-fx
 ::on-revoke-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to revoke MCP connection" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking-id) nil)
            (assoc-in (conj sub/root-path :revoke-error)
                      (error-message err "Failed to revoke connection")))}))

;;
;; Revoke all MCP connections
;;

(rf/reg-event-fx
 ::revoke-all-connections
 (fn [{db :db} _]
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking-all?) true)
            (assoc-in (conj sub/root-path :revoke-error) nil))
    ::ws-revoke-all-connections {}}))

(rf/reg-fx
 ::ws-revoke-all-connections
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/revoke-all-mcp-connections
     :body       {}
     :on-success [::on-all-revoked]
     :on-error   [::on-revoke-all-error]})))

(rf/reg-event-fx
 ::on-all-revoked
 (fn [{db :db} _]
   {:db (assoc-in db (conj sub/root-path :revoking-all?) false)
    :fx [[:dispatch [::load-connections]]
         [:dispatch [::load-sessions]]]}))

(rf/reg-event-fx
 ::on-revoke-all-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to revoke all MCP connections" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :revoking-all?) false)
            (assoc-in (conj sub/root-path :revoke-error)
                      (error-message err "Failed to revoke all connections")))}))

;;
;; Live sessions
;;

(rf/reg-event-fx
 ::load-sessions
 (fn [_ _]
   {::ws-load-sessions {}}))

(rf/reg-fx
 ::ws-load-sessions
 (fn [_]
   (ws/ws-send!
    {:id         :plugin/list-mcp-sessions
     :body       {}
     :on-success [::on-sessions-loaded]
     :on-error   [::on-sessions-error]})))

(rf/reg-event-fx
 ::on-sessions-loaded
 (fn [{db :db} [_ resp]]
   {:db (-> db
            (assoc-in (conj sub/root-path :sessions-loading?) false)
            (assoc-in (conj sub/root-path :sessions) (-> resp :body :sessions)))}))

(rf/reg-event-fx
 ::on-sessions-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to load MCP sessions" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :sessions-loading?) false)
            (assoc-in (conj sub/root-path :sessions-error)
                      (error-message err "Failed to load sessions")))}))

(rf/reg-event-fx
 ::terminate-session
 (fn [{db :db} [_ session-id]]
   {:db (-> db
            (assoc-in (conj sub/root-path :terminating-session-id) session-id)
            (assoc-in (conj sub/root-path :sessions-error) nil))
    ::ws-terminate-session {:session-id session-id}}))

(rf/reg-fx
 ::ws-terminate-session
 (fn [{:keys [session-id]}]
   (ws/ws-send!
    {:id         :plugin/terminate-mcp-session
     :body       {:session-id session-id}
     :on-success [::on-session-terminated]
     :on-error   [::on-terminate-session-error]})))

(rf/reg-event-fx
 ::on-session-terminated
 (fn [{db :db} _]
   {:db (assoc-in db (conj sub/root-path :terminating-session-id) nil)
    :fx [[:dispatch [::load-sessions]]]}))

(rf/reg-event-fx
 ::on-terminate-session-error
 (fn [{db :db} [_ err]]
   (logging/error "Failed to terminate MCP session" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :terminating-session-id) nil)
            (assoc-in (conj sub/root-path :sessions-error)
                      (error-message err "Failed to terminate session")))}))

;;
;; MCP undo
;;

(rf/reg-event-db
 ::set-undo-tx-id-input
 (fn [db [_ value]]
   (-> db
       (assoc-in (conj sub/root-path :undo-tx-id-input) value)
       (assoc-in (conj sub/root-path :undo-preview) nil)
       (assoc-in (conj sub/root-path :undo-result) nil)
       (assoc-in (conj sub/root-path :undo-error) nil))))

(rf/reg-event-fx
 ::preview-undo
 (fn [{db :db} [_ tx-id]]
   {:db (-> db
            (assoc-in (conj sub/root-path :undo-loading?) true)
            (assoc-in (conj sub/root-path :undo-error) nil)
            (assoc-in (conj sub/root-path :undo-preview) nil)
            (assoc-in (conj sub/root-path :undo-result) nil))
    ::ws-undo-transaction {:tx-id tx-id :dry-run? true}}))

(rf/reg-event-fx
 ::execute-undo
 (fn [{db :db} [_ tx-id]]
   {:db (-> db
            (assoc-in (conj sub/root-path :undo-loading?) true)
            (assoc-in (conj sub/root-path :undo-error) nil))
    ::ws-undo-transaction {:tx-id tx-id :dry-run? false}}))

(rf/reg-fx
 ::ws-undo-transaction
 (fn [{:keys [tx-id dry-run?]}]
   (ws/ws-send!
    {:id         :plugin/undo-mcp-transaction
     :body       {:tx-id tx-id :dry-run? dry-run?}
     :on-success (if dry-run?
                   [::on-undo-previewed]
                   [::on-undo-executed])
     :on-error   [::on-undo-error]})))

(rf/reg-event-fx
 ::on-undo-previewed
 (fn [{db :db} [_ resp]]
   {:db (-> db
            (assoc-in (conj sub/root-path :undo-loading?) false)
            (assoc-in (conj sub/root-path :undo-preview) (:body resp)))}))

(rf/reg-event-fx
 ::on-undo-executed
 (fn [{db :db} [_ resp]]
   {:db (-> db
            (assoc-in (conj sub/root-path :undo-loading?) false)
            (assoc-in (conj sub/root-path :undo-preview) nil)
            (assoc-in (conj sub/root-path :undo-result) (:body resp)))}))

(rf/reg-event-fx
 ::on-undo-error
 (fn [{db :db} [_ err]]
   (logging/error "MCP undo failed" err)
   {:db (-> db
            (assoc-in (conj sub/root-path :undo-loading?) false)
            (assoc-in (conj sub/root-path :undo-error)
                      (error-message err "Undo failed")))}))
